import {
  Box,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Text,
} from "@chakra-ui/react";
import { useStytch } from "@stytch/react";
import { useQuery } from "@tanstack/react-query";
import {
  AuthFactorInstanceProps,
  OtpAuthFactorInstance,
} from "features/auth/AuthFactor";
import { AuthHelpButton } from "features/auth/AuthHelpButton";
import { AuthOtpFormData } from "features/auth/AuthTypes";
import {
  getStytchSessionDurationMinutes,
  useStytchFormErrorHandler,
} from "features/auth/StytchUtils";
import { parsePhoneNumber } from "libphonenumber-js";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import { CardCloseButton } from "shared/components/card/CardCloseButton";
import Form from "shared/components/form/Form";
import { FormNumberInput } from "shared/components/input/text/NumberInput";
import { queryOptionsSingleShot } from "shared/utils/QueryUtils";

interface AuthFactorOtpProps
  extends AuthFactorInstanceProps<OtpAuthFactorInstance> {
  sendCode?: boolean;
}

export const AuthFactorOtp: React.FC<AuthFactorOtpProps> = ({
  onCancel,
  onSuccess,
  instance,
  sendCode = true,
  rememberDevice,
}) => {
  const { t } = useTranslation(["common", "auth"]);
  const stytchClient = useStytch();
  const onStytchError = useStytchFormErrorHandler();

  const form = useForm<AuthOtpFormData>();

  const onSubmit = (formData: AuthOtpFormData) =>
    stytchClient.otps.authenticate(formData.code, instance.data.phone_id, {
      session_duration_minutes: getStytchSessionDurationMinutes(rememberDevice),
    });

  const phone = useMemo(
    () => parsePhoneNumber(instance.data.phone_number, "US"),
    [instance.data.phone_number],
  );

  useQuery({
    queryKey: ["auth", "otp", "sms", phone.number],
    queryFn: () => stytchClient.otps.sms.send(phone.number),
    ...queryOptionsSingleShot,
    enabled: sendCode,
  });

  return (
    <Form
      form={form}
      disableNavBlocker={true}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
      onError={onStytchError}
    >
      <Card width="md">
        <CardHeader>
          <Box>
            <Text textStyle="h4">{t("auth:authenticate")}</Text>
            <Text pt="24px">
              <Trans
                ns="auth"
                i18nKey="otpInstructions"
                values={{ phone: phone.formatNational() }}
              >
                Enter code sent to
                <Text as="span" textStyle="bodyBold">
                  fone
                </Text>
              </Trans>
            </Text>
          </Box>
          <CardCloseButton onClick={onCancel} />
        </CardHeader>
        <CardBody pb={0}>
          <FormNumberInput
            name="code"
            title={t("auth:code")}
            autoComplete="one-time-code"
            autoFocus={true}
            maxLength={6}
            allowLeadingZeros={true}
            thousandSeparator={false}
            hideIcon={true}
          />
        </CardBody>
        <CardFooter>
          <AuthHelpButton />
          <FormSubmitButton>{t("common:continue")}</FormSubmitButton>
        </CardFooter>
      </Card>
    </Form>
  );
};
